import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, catchError, debounceTime, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { SvgComponent } from '@components/svg.component';
import { AppRoutes } from '@helpers/app-routes';
import { OrderPaymentStatus, OrderStatusFilter } from '@models/order/order.model';
import { LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from '@helpers/list-config';
import { PaginationInfo } from '@models/shared/pagination-info.model';
import {
  SessionOrderListFilters,
  SessionOrderModel,
  SessionOrderSummaryModel,
} from '@models/session-order/session-order.model';
import { SessionOrderService } from '@services/session-order.service';
import { SessionService } from '@services/session.service';
import { DatePipe } from '@angular/common';
import { OrderListHeaderComponent } from './order-list-header.component';
import { FormatPricePipe } from '@pipes/format-price.pipe';
import { LoaderComponent } from '@components/app-loader/app-loader.component';
import { PaginationComponent } from '@components/pagination/pagination.component';
import { ModalForceReasonComponent } from '@components/Modals/modal-force-reason/modal-force-reason.component';

@Component({
  selector: 'app-order-list-page',
  imports: [
    TranslateModule,
    SvgComponent,
    LoaderComponent,
    PaginationComponent,
    DatePipe,
    OrderListHeaderComponent,
    FormatPricePipe,
    ModalForceReasonComponent,
  ],
  templateUrl: './order-list-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'block h-full' },
})
export class OrderListPageComponent {
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);
  private readonly sessionOrderService = inject(SessionOrderService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  private readonly pageRequests = new Subject<number>();
  private readonly searchChanges = new Subject<string>();

  protected readonly orders = signal<SessionOrderModel[]>([]);
  protected readonly pagination = signal<PaginationInfo | null>(null);
  protected readonly summary = signal<SessionOrderSummaryModel | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly activeFilter = signal<OrderStatusFilter>('all');
  protected readonly searchQuery = signal('');
  protected readonly cancelReasonModalOpen = signal(false);
  #sessionId: number | null = null;
  #pendingCancelSessionOrderId: number | null = null;

  protected readonly filterCounts = computed<Partial<Record<OrderStatusFilter, number>>>(
    () => this.summary()?.counts ?? {},
  );

  protected readonly totalCollected = computed(() => this.summary()?.total_collected ?? 0);

  constructor() {
    this.pageRequests
      .pipe(
        tap(() => this.isLoading.set(true)),
        switchMap((page) => {
          const sessionId = this.#sessionId;
          if (sessionId === null) return EMPTY;

          return this.sessionOrderService
            .getSessionOrders(sessionId, page, LIST_PAGE_SIZE, this.#currentFilters())
            .pipe(catchError(() => this.#onLoadError()));
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        const { page } = result.pagination;
        if (result.data.length === 0 && page > 1) {
          this.loadPage(page - 1);
          return;
        }

        this.orders.set(result.data);
        this.pagination.set(result.pagination);
        this.summary.set(result.summary);
        this.isLoading.set(false);
      });

    this.searchChanges
      .pipe(debounceTime(SEARCH_DEBOUNCE_MS), takeUntilDestroyed())
      .subscribe(() => this.loadPage(1));

    this.sessionService
      .getLastSession()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: (session) => {
          this.#sessionId = session.id;
          this.loadPage(1);
        },
        error: () => void this.#onLoadError(),
      });
  }

  protected loadPage(page: number): void {
    this.pageRequests.next(page);
  }

  protected setFilter(filter: OrderStatusFilter): void {
    this.activeFilter.set(filter);
    this.loadPage(1);
  }

  protected onSearchChange(query: string): void {
    this.searchQuery.set(query);
    this.searchChanges.next(query);
  }

  #currentFilters(): SessionOrderListFilters {
    return { status: this.activeFilter(), search: this.searchQuery() };
  }

  #onLoadError(): Observable<never> {
    this.isLoading.set(false);
    this.toastr.error(
      this.translate.instant('order_list.errors.load_error_message'),
      this.translate.instant('order_list.errors.load_error_title'),
    );
    return EMPTY;
  }

  protected getStatusClass(status: OrderPaymentStatus): string {
    const classes: Record<OrderPaymentStatus, string> = {
      valid: 'bg-vert-1',
      waiting: 'bg-orange-1',
      forced: 'bg-rose-1',
      cancelled: 'bg-gris-1',
    };
    return classes[status];
  }

  protected getPaymentMode(payments: Record<string, number> | null): string {
    if (!payments) return '—';
    return Object.keys(payments)[0] ?? '—';
  }

  protected goToOrderDetail(sessionOrderId: number): void {
    this.router.navigate([AppRoutes.orderDetail, sessionOrderId]);
  }

  protected cancelOrder(sessionOrderId: number): void {
    this.#pendingCancelSessionOrderId = sessionOrderId;
    this.cancelReasonModalOpen.set(true);
  }

  protected onCancelReasonModalClose(): void {
    this.cancelReasonModalOpen.set(false);
    this.#pendingCancelSessionOrderId = null;
  }

  protected onConfirmCancelReason(reason: string): void {
    const sessionOrderId = this.#pendingCancelSessionOrderId;
    if (sessionOrderId === null) return;

    this.cancelReasonModalOpen.set(false);
    this.#pendingCancelSessionOrderId = null;

    this.sessionOrderService.cancelSessionOrder(sessionOrderId, reason).subscribe({
      next: () => {
        this.loadPage(this.pagination()?.page ?? 1);
        this.toastr.success(
          this.translate.instant('order_list.success.cancel_success_message'),
          this.translate.instant('order_list.success.cancel_success_title'),
        );
      },
      error: () => {
        this.toastr.error(
          this.translate.instant('order_list.errors.cancel_error_message'),
          this.translate.instant('order_list.errors.cancel_error_title'),
        );
      },
    });
  }
}
